import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {
    Text,
    Stack,
    Accordion,
    Box,
    AccordionButton,
    Image,
    AccordionPanel,
    Grid,
    Button,
    Link,
    Flex,
    AccordionItem,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../utils/cloudinary-utils'
import CloudinaryImage from '../../cloudinary-media/image'
import useTagManager from '../../../hooks/use-tag-manager'
import {
    ProductRatingSummary,
    handleProductReviewsClick,
    shouldPromoteProductRating
} from './product-rating'

const NECKLACE_CLASS_CODES = new Set(['3005', '5005'])

const PREVIEW_SECTIONS = new Set(['description', 'details'])
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

const isNecklaceProduct = (product) => NECKLACE_CLASS_CODES.has(String(product?.c_classCode))

const isPreviewHidden = (el) => {
    if (!el) return false
    const previewActive =
        getComputedStyle(el).webkitLineClamp !== 'none' ||
        el.classList.contains('details-collapsed')
    if (!previewActive) return null
    return el.textContent.length > el.innerText.length || el.scrollHeight > el.clientHeight + 1
}

/** Line-clamp is unreliable on iOS ≤15 — skip Notes truncation there (same as old TruncatedText). */
const useIsIOS15OrBelow = () => {
    const [isIOS15, setIsIOS15] = useState(false)
    useEffect(() => {
        if (typeof navigator === 'undefined') return
        const isIOS =
            /iP(hone|od|ad)/.test(navigator.platform) ||
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
        if (!isIOS) return
        const match = navigator.userAgent.match(/OS (\d+)_/)
        if (match && parseInt(match[1], 10) <= 15) {
            setIsIOS15(true)
        }
    }, [])
    return isIOS15
}

const ExpandIcon = ({expanded}) => (
    <Image
        alt=""
        width="15px"
        height="15px"
        className="accordion-image"
        color="black"
        verticalAlign="middle"
        src={getUrl(
            expanded ? 'UIImages/images/minus-large.svg' : 'UIImages/images/plus-large.svg'
        )}
    />
)

ExpandIcon.propTypes = {expanded: PropTypes.bool}

/** Put Size Guide on the Size attribute row when CMS has a Size block. */
const withInlineSizeGuide = (html, sizeGuideLabel) => {
    if (!html) return {html, hasInlineSizeGuide: false}

    const sizeAt = html.indexOf('<h3>Size</h3>')
    if (sizeAt === -1) return {html, hasInlineSizeGuide: false}

    const pairEnd = html.indexOf('</p>', sizeAt)
    if (pairEnd === -1) return {html, hasInlineSizeGuide: false}

    const end = pairEnd + 4
    const icon = getUrl('UIImages/images/ruler.svg')
    const row = `<div class="details-size-row"><span class="details-size-copy">${html.slice(
        sizeAt,
        end
    )}</span><button type="button" class="size-guide-inline" data-size-guide="true"><img src="${icon}" alt="" width="16" height="10" />${sizeGuideLabel}</button></div>`

    return {
        html: `${html.slice(0, sizeAt)}${row}${html.slice(end)}`,
        hasInlineSizeGuide: true
    }
}

const SECTION_TITLE_PROPS = {
    fontFamily: 'caslon',
    fontSize: '23px',
    fontWeight: '500',
    letterSpacing: '0.115px',
    lineHeight: 'normal'
}

export const ProductDetailsAccordion = ({
    product,
    galleryRef,
    productDetailsRef,
    isSet = false,
    isEngagementProduct,
    onOpenSizeGuide
}) => {
    let accordionConfigJson = null
    const location = useLocation()
    const intl = useIntl()
    const tagManager = useTagManager()
    const styles = useStyleConfig('ProductDetailsAccordion')
    const ratingStyles = useStyleConfig('ProductRating')
    const isIOS15 = useIsIOS15OrBelow()
    const previewEls = useRef({})
    const [previewHidden, setPreviewHidden] = useState({})
    const [activeHash, setActiveHash] = useState('')
    const shouldHideReviews = isSet || isEngagementProduct || shouldPromoteProductRating(product)
    const sizeGuideLabel = intl.formatMessage({
        id: 'size.guide',
        defaultMessage: 'Size Guide'
    })

    try {
        accordionConfigJson = JSON.parse(product.c_pdpAccordionConfig)
        if (shouldHideReviews) {
            accordionConfigJson = accordionConfigJson.filter((i) => i.id !== 'reviews')
        }
    } catch (e) {
        console.error('Error parsing PDP Accordion configuration', e)
    }

    useIsomorphicLayoutEffect(() => {
        setActiveHash(location?.hash || '')
    }, [location?.hash, product?.id])

    useEffect(() => {
        if (!activeHash) return

        let position
        const domElement = document.querySelector(activeHash)

        if (!domElement || !galleryRef?.current || !productDetailsRef?.current) return

        const galleryHeight = galleryRef.current.getBoundingClientRect().height
        const detailsHeight = productDetailsRef.current.getBoundingClientRect().height

        if (galleryHeight > detailsHeight) {
            position =
                galleryHeight -
                domElement.offsetTop -
                domElement.getBoundingClientRect().height -
                100
        } else {
            position = domElement.offsetTop
        }

        const scrollTimeout = window.setTimeout(() => {
            window.scrollTo({
                top: position,
                behavior: 'smooth'
            })
        }, 200)

        return () => window.clearTimeout(scrollTimeout)
    }, [activeHash, galleryRef, productDetailsRef])

    useEffect(() => {
        let observer
        const measure = () => {
            setPreviewHidden((prev) => {
                let changed = false
                const next = {...prev}
                PREVIEW_SECTIONS.forEach((id) => {
                    if (id === 'description' && isIOS15) {
                        if (next[id] !== false) {
                            next[id] = false
                            changed = true
                        }
                        return
                    }
                    const hidden = isPreviewHidden(previewEls.current[id])

                    if (hidden === null || next[id] === hidden) return
                    next[id] = hidden
                    changed = true
                })
                return changed ? next : prev
            })
        }

        const frame = requestAnimationFrame(() => {
            measure()
            if (typeof ResizeObserver === 'undefined') return
            observer = new ResizeObserver(measure)
            PREVIEW_SECTIONS.forEach((id) => {
                const el = previewEls.current[id]
                if (el) observer.observe(el)
            })
        })
        window.addEventListener('resize', measure)

        return () => {
            cancelAnimationFrame(frame)
            observer?.disconnect()
            window.removeEventListener('resize', measure)
        }
    }, [product?.id, isIOS15])

    if (!accordionConfigJson) {
        return <></>
    }

    accordionConfigJson = accordionConfigJson
        ?.filter((item) => !!item.title)
        ?.filter((section) => {
            return !section.productAttrID || product?.[section.productAttrID]
        })

    const expandedSections = accordionConfigJson?.reduce((acc, current, currentIndex) => {
        const linked = activeHash.indexOf(current.id) !== -1
        if (PREVIEW_SECTIONS.has(current.id)) {
            if (linked) acc.push(currentIndex)
            return acc
        }
        if (current.isExpanded || linked) {
            acc.push(currentIndex)
        }
        return acc
    }, [])

    return (
        <Stack
            gap="0px"
            width="100%"
            className="accordion-stack"
            marginX="auto"
            sx={{
                '.chakra-accordion': {
                    borderColor: 'gray.700',
                    borderTopWidth: '0px'
                },
                'button[disabled].chakra-accordion__button': {
                    cursor: 'default',
                    opacity: 1
                },
                '.accordion-wrapper': {
                    borderColor: 'gray.700'
                },
                '.accordion-wrapper:last-of-type': {
                    borderBottomWidth: '1px'
                },
                '.chakra-accordion__item': {
                    borderWidth: '0px'
                },
                '.chakra-accordion__item:last-of-type': {
                    borderBottomWidth: '0px'
                },
                // Keep Notes/Details panels visible; +/- only switches preview vs full content.
                '#pro-description-tab .chakra-collapse, #pro-details-tab .chakra-collapse': {
                    display: 'block !important',
                    height: 'auto !important',
                    overflow: 'visible !important',
                    opacity: '1 !important'
                }
            }}
        >
            <Accordion
                key={`${product?.id}-${shouldHideReviews}-${activeHash}`}
                variant="accordion"
                className="accordion"
                allowMultiple={true}
                defaultIndex={expandedSections}
                sx={{
                    '#pro-reviews-tab .chakra-collapse': {
                        display: 'none !important'
                    }
                }}
            >
                {accordionConfigJson.map((item, index) => {
                    const accordionContent = item.productAttrID
                        ? product?.[item.productAttrID]
                        : item.content
                    const showItem = item.id === 'callout' ? product?.c_productPageCallout : true
                    const canShowSizeGuide =
                        item.id === 'details' &&
                        isNecklaceProduct(product) &&
                        !!product?.c_sizeChart &&
                        typeof onOpenSizeGuide === 'function'
                    const isPreviewSection = PREVIEW_SECTIONS.has(item.id)
                    const preparedDetails = canShowSizeGuide
                        ? withInlineSizeGuide(accordionContent, sizeGuideLabel)
                        : null
                    const panelHtml =
                        item.id === 'details'
                            ? preparedDetails?.html ?? accordionContent
                            : accordionContent
                    const hasInlineSizeGuide = !!preparedDetails?.hasInlineSizeGuide
                    const showHeaderSizeGuide = canShowSizeGuide && !hasInlineSizeGuide
                    const isExpandable =
                        item.isExpandable || (isPreviewSection && !!previewHidden[item.id])

                    return (
                        showItem && (
                            <Box
                                key={item.id}
                                id={`pro-${item.id}-tab`}
                                className="accordion-wrapper"
                                data-index={index}
                                borderBottomWidth="1px"
                            >
                                <AccordionItem
                                    isDisabled={!isExpandable && item.id !== 'reviews'}
                                    display="grid"
                                    className="accordion-item"
                                    minWidth={0}
                                    width="100%"
                                >
                                    {({isExpanded}) => (
                                        <>
                                            {item.id === 'reviews' ? (
                                                <AccordionButton
                                                    id="pdp-rating-summary"
                                                    data-monetate-id="pdp-rating-summary"
                                                    data-rating-placement="reviews"
                                                    padding={0}
                                                    width="100%"
                                                    className="reviews-header"
                                                    sx={ratingStyles.trigger}
                                                    onClick={(event) =>
                                                        handleProductReviewsClick(
                                                            event,
                                                            product,
                                                            tagManager
                                                        )
                                                    }
                                                >
                                                    <Box
                                                        color="black"
                                                        className="accordion-box"
                                                        paddingTop={{base: '0px', lg: '20px'}}
                                                        paddingBottom="15px"
                                                        justifyContent="space-between"
                                                    >
                                                        <Text
                                                            as="span"
                                                            itemProp="name"
                                                            className="accordion-text"
                                                            fontFamily="caslon"
                                                            fontSize="23px"
                                                            fontWeight="500"
                                                            lineHeight="normal"
                                                            letterSpacing="0.115px"
                                                        >
                                                            {item.title}
                                                            <ProductRatingSummary
                                                                product={product}
                                                            />
                                                        </Text>
                                                    </Box>
                                                </AccordionButton>
                                            ) : (
                                                <Flex alignItems="center" width="100%">
                                                    <AccordionButton
                                                        paddingRight={0}
                                                        className="accordion-button"
                                                        flex="1"
                                                        minWidth="0"
                                                        onClick={() => {
                                                            if (isPreviewSection) {
                                                                tagManager.pdpCtaClick(
                                                                    isExpanded
                                                                        ? 'cta click - Read Less'
                                                                        : 'cta click - Read More',
                                                                    product?.name
                                                                )
                                                            } else {
                                                                tagManager.pdpCtaClick(
                                                                    `cta click - ${item.title}`,
                                                                    product?.name
                                                                )
                                                            }
                                                        }}
                                                    >
                                                        <Text
                                                            as="span"
                                                            itemProp="name"
                                                            className="accordion-text"
                                                            {...SECTION_TITLE_PROPS}
                                                        >
                                                            {item.title}
                                                        </Text>

                                                        {isExpandable && (
                                                            <ExpandIcon expanded={isExpanded} />
                                                        )}
                                                    </AccordionButton>

                                                    {showHeaderSizeGuide && (
                                                        <Box
                                                            as="button"
                                                            type="button"
                                                            onClick={onOpenSizeGuide}
                                                            className="size-guide-inline"
                                                            {...styles.sizeGuideFallback}
                                                        >
                                                            <Image
                                                                src={getUrl(
                                                                    'UIImages/images/ruler.svg'
                                                                )}
                                                                alt=""
                                                                width="16px"
                                                                height="10px"
                                                            />
                                                            {sizeGuideLabel}
                                                        </Box>
                                                    )}
                                                </Flex>
                                            )}

                                            <AccordionPanel
                                                className="accordion-pannel"
                                                width="100%"
                                                minWidth={0}
                                            >
                                                <Grid gap="20px" className="accordion-grid">
                                                    {item.imageUrl && (
                                                        <Box
                                                            itemProp="image"
                                                            className="accordion-box"
                                                        >
                                                            <CloudinaryImage
                                                                src={item.imageUrl}
                                                                imgSizes={{
                                                                    base: '100vw',
                                                                    xl: '1100px'
                                                                }}
                                                                sizes={[320, 374, 435, 780, 1100]}
                                                                alt={item.imgAlt}
                                                            />
                                                        </Box>
                                                    )}

                                                    <Stack
                                                        gridArea={'auto'}
                                                        gap="20px"
                                                        className="accordion-grid-stack"
                                                        minWidth={0}
                                                        width="100%"
                                                    >
                                                        <Box
                                                            ref={
                                                                isPreviewSection
                                                                    ? (el) => {
                                                                          previewEls.current[
                                                                              item.id
                                                                          ] = el
                                                                      }
                                                                    : undefined
                                                            }
                                                            itemProp="text"
                                                            className={
                                                                item.id === 'details' && !isExpanded
                                                                    ? 'details-collapsed'
                                                                    : undefined
                                                            }
                                                            dangerouslySetInnerHTML={{
                                                                __html: panelHtml
                                                            }}
                                                            noOfLines={
                                                                item.id === 'description' &&
                                                                !isExpanded &&
                                                                !isIOS15
                                                                    ? 3
                                                                    : undefined
                                                            }
                                                            onClick={
                                                                hasInlineSizeGuide
                                                                    ? (event) => {
                                                                          if (
                                                                              event.target.closest(
                                                                                  '[data-size-guide]'
                                                                              )
                                                                          ) {
                                                                              onOpenSizeGuide()
                                                                          }
                                                                      }
                                                                    : undefined
                                                            }
                                                            {...styles.content}
                                                        />

                                                        {item.buttonLink && item.buttonText ? (
                                                            <Box className="accordion-grid-button-box">
                                                                <Button
                                                                    as={Link}
                                                                    className="accordion-grid-button"
                                                                    href={item.buttonLink}
                                                                    itemProp="url"
                                                                    variant={'outline'}
                                                                    size="small"
                                                                    paddingLeft="inherit"
                                                                >
                                                                    {item.buttonText}
                                                                </Button>
                                                            </Box>
                                                        ) : null}
                                                    </Stack>
                                                </Grid>
                                            </AccordionPanel>
                                        </>
                                    )}
                                </AccordionItem>
                            </Box>
                        )
                    )
                })}
            </Accordion>
        </Stack>
    )
}

ProductDetailsAccordion.propTypes = {
    product: PropTypes.object,
    isSet: PropTypes.bool,
    galleryRef: PropTypes.object,
    productDetailsRef: PropTypes.object,
    isEngagementProduct: PropTypes.bool,
    onOpenSizeGuide: PropTypes.func
}

export default ProductDetailsAccordion
